import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertCircle, ChevronLeft, LayoutGrid, List } from 'lucide-react';
import { clsx } from 'clsx';
import { getProgramBySlug } from '@/config/programs';
import { apiConfig } from '@/config/apiConfig';
import { useAuth } from '@/hooks/useAuth';
import { useResourceMeta } from './hooks/useResourceMeta';
import { useSubmissions } from './hooks/useSubmissions';
import { hasProgramAccess } from './utils/programResource';
import { ResourceGrid } from './components/ResourceGrid/ResourceGrid';
import { ResourceGridFilters } from './components/ResourceGrid/ResourceGridFilters';
import { ResourceGridPagination } from './components/ResourceGrid/ResourceGridPagination';
import { ResourceSummaryCards } from './components/ResourceGrid/ResourceSummaryCards';
import { ResourceEditDialog } from './components/ResourceEditDialog/ResourceEditDialog';
import { ResourceHistoryDrawer } from './components/ResourceHistoryDrawer/ResourceHistoryDrawer';
import { ResourceDetailDrawer } from './components/ResourceGrid/ResourceDetailDrawer';
import type { SubmissionListParams, SubmissionRow } from './types/submission';

type ViewMode = 'grid' | 'cards';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Full review page — meta-driven grid, dynamic filters, edit dialog,
 * history drawer. All UI state (filters, sort, page, current dialog)
 * lives locally; the hooks handle data.
 */
export function ResourceReviewPage() {
  const { programCode: slug, resource } = useParams<{
    programCode: string;
    resource: string;
  }>();
  const { user } = useAuth();

  const program = getProgramBySlug(slug);
  const featureOn = apiConfig.features.enableImpactDataReview;

  // Filter / sort / page / view state — lives in one blob so the
  // useSubmissions effect gets a stable string key and refetches only
  // on value change. `filters` is keyed by the query-string `param`
  // each declared filter maps to (search, date, location subfields,
  // etc.) so the shape sent to the wire matches whatever the meta
  // declares.
  //
  // Sort is a single object rather than two independent atoms so
  // `handleSort`'s "toggle direction on repeat click" doesn't race
  // against the ordering state — reading both from the updater
  // callback is guaranteed to see the latest value.
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sortState, setSortState] = useState<{ column: string; order: 'asc' | 'desc' } | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  // Dialogs — one row can be either edited or inspected in history at
  // a time; we track them independently so the drawer can stay open
  // while the reviewer opens the edit dialog from another row.
  const [editingRow, setEditingRow] = useState<SubmissionRow | null>(null);
  const [historyRow, setHistoryRow] = useState<SubmissionRow | null>(null);
  const [detailRow, setDetailRow] = useState<SubmissionRow | null>(null);

  const metaEnabled = featureOn && Boolean(program) && hasProgramAccess(program ?? { slug: 'tbtp', label: '', codes: [] }, user?.roles);
  const { meta, loading: metaLoading, error: metaError } = useResourceMeta(metaEnabled ? resource : undefined);

  const listParams = useMemo<SubmissionListParams>(
    () => ({
      ...filters,
      sort: sortState?.column,
      sort_order: sortState?.order,
      page,
      page_size: pageSize,
      summary: viewMode === 'cards' ? true : undefined,
    }),
    [filters, sortState, page, pageSize, viewMode],
  );

  const {
    page: submissions,
    loading: listLoading,
    error: listError,
    refetch: refetchList,
  } = useSubmissions(metaEnabled ? resource : undefined, listParams);

  const handleSort = useCallback((column: string) => {
    setPage(1);
    setSortState((current) => {
      if (current?.column === column) {
        // Repeat click on the same column toggles direction.
        return { column, order: current.order === 'asc' ? 'desc' : 'asc' };
      }
      return { column, order: 'asc' };
    });
  }, []);

  const handleFiltersChange = useCallback((next: Record<string, string>) => {
    setPage(1);
    setFilters(next);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPage(1);
    setPageSize(size);
  }, []);

  const handleViewModeChange = useCallback((next: ViewMode) => {
    setPage(1);
    setViewMode(next);
  }, []);

  // ── Gates (identical to phase-1 shell) ─────────────────────────────
  if (!program) return <NotFoundState />;
  if (!hasProgramAccess(program, user?.roles)) return <ForbiddenState label={program.label} />;
  if (!featureOn) return <DisabledState label={program.label} />;
  if (!resource) return <NotFoundState />;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs text-rgf-gray-500">
            <Link
              to={`/programs/${program.slug}/impact-data`}
              className="inline-flex items-center gap-1 hover:text-rgf-primary-600"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              {program.label}
            </Link>
            <span>·</span>
            <span>Impact Data</span>
          </div>
          <h1 className="mt-1 text-2xl font-semibold text-rgf-gray-900">
            {meta?.display_title || humanise(resource)}
          </h1>
          <p className="mt-1 text-sm text-rgf-gray-500">{resource}</p>
        </div>
        {meta?.view_meta?.summary_view && (
          <ViewModeToggle mode={viewMode} onChange={handleViewModeChange} />
        )}
      </header>

      {metaError && <ErrorCard title="Couldn't load resource metadata" message={metaError.message} />}
      {metaLoading && !meta && <LoadingCard message="Loading resource metadata…" />}

      {meta && (
        <>
          <ResourceGridFilters
            meta={meta}
            values={filters}
            onChange={handleFiltersChange}
          />

          {listError && <ErrorCard title="Couldn't load submissions" message={listError.message} />}

          {viewMode === 'cards' ? (
            <ResourceSummaryCards
              meta={meta}
              page={submissions}
              loading={listLoading}
              // Card view intentionally has no Edit — cards render
              // from `?summary=true` which omits `updated_at`, and
              // PATCHing without it triggers a conflict rejection.
              // The View Details button opens the drawer, which
              // re-fetches the full row (with updated_at) and
              // offers Edit from there.
              onViewRow={(row) => setDetailRow(row)}
              onHistoryRow={(row) => setHistoryRow(row)}
            />
          ) : (
            <ResourceGrid
              meta={meta}
              parentResource={resource}
              page={submissions}
              loading={listLoading}
              sort={sortState?.column}
              sortOrder={sortState?.order}
              onSort={handleSort}
              onEditRow={(row) => setEditingRow(row)}
              onHistoryRow={(row) => setHistoryRow(row)}
              onViewRow={(row) => setDetailRow(row)}
            />
          )}

          <ResourceGridPagination
            page={submissions}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}

      {meta && editingRow && (
        <ResourceEditDialog
          meta={meta}
          resource={resource}
          row={editingRow}
          onClose={() => setEditingRow(null)}
          onSaved={refetchList}
        />
      )}

      {historyRow && (
        <ResourceHistoryDrawer
          resource={resource}
          row={historyRow}
          onClose={() => setHistoryRow(null)}
          onRestored={refetchList}
        />
      )}

      {meta && detailRow && (
        <ResourceDetailDrawer
          meta={meta}
          parentResource={resource}
          row={detailRow}
          onClose={() => setDetailRow(null)}
          onEdit={(fresh) => {
            // Close the drawer first so the modal focus lock takes
            // over cleanly; then hand the edit dialog the freshest
            // row we have. ResourceEditDialog re-fetches anyway to
            // pick up any changes since the drawer opened.
            setDetailRow(null);
            setEditingRow(fresh);
          }}
        />
      )}
    </div>
  );
}

function humanise(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function ViewModeToggle({
  mode,
  onChange,
}: {
  mode: ViewMode;
  onChange: (mode: ViewMode) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-lg border border-rgf-gray-200 bg-white">
      <ViewModeButton
        active={mode === 'grid'}
        onClick={() => onChange('grid')}
        icon={<List className="h-4 w-4" />}
        label="Grid"
      />
      <ViewModeButton
        active={mode === 'cards'}
        onClick={() => onChange('cards')}
        icon={<LayoutGrid className="h-4 w-4" />}
        label="Cards"
      />
    </div>
  );
}

function ViewModeButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition',
        active
          ? 'bg-rgf-primary-50 text-rgf-primary-700'
          : 'text-rgf-gray-600 hover:bg-rgf-gray-50 hover:text-rgf-gray-900',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function LoadingCard({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-rgf-gray-200 bg-white p-6 text-sm text-rgf-gray-500">
      {message}
    </div>
  );
}

function ErrorCard({ title, message }: { title: string; message: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
      <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600" />
      <div className="text-sm">
        <p className="font-medium text-red-900">{title}</p>
        <p className="mt-1 text-red-700">{message}</p>
      </div>
    </div>
  );
}

function NotFoundState() {
  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="rounded-xl border border-rgf-gray-200 bg-white p-8 text-center">
        <h2 className="text-lg font-semibold text-rgf-gray-900">Not found</h2>
        <p className="mt-2 text-sm text-rgf-gray-600">
          The program or resource you tried to open doesn't exist.
        </p>
      </div>
    </div>
  );
}

function ForbiddenState({ label }: { label: string }) {
  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="rounded-xl border border-rgf-gray-200 bg-white p-8 text-center">
        <h2 className="text-lg font-semibold text-rgf-gray-900">You don't have access</h2>
        <p className="mt-2 text-sm text-rgf-gray-600">
          You need a {label} role to open its Impact Data.
        </p>
      </div>
    </div>
  );
}

function DisabledState({ label }: { label: string }) {
  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="rounded-xl border border-rgf-gray-200 bg-white p-8 text-center">
        <h2 className="text-lg font-semibold text-rgf-gray-900">Not enabled yet</h2>
        <p className="mt-2 text-sm text-rgf-gray-600">
          Impact Data review for {label} isn't turned on in this environment.
        </p>
      </div>
    </div>
  );
}
